import {
  AspectRatio,
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  DialogPortal,
  Sheet,
  SheetContent,
  SheetTrigger,
  SHEET_CONTENT_SIDE,
  mediaQueries,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  BUTTON_SIZE,
  Link,
} from "@mozioinc/ui";
import { Link as LinkBase } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "usehooks-ts";
import { AirportTransferIcon } from "./airport-transfer-icon";
import { ReactComponent as ChevronSvg } from "./assets/chevron.svg";
import { ReactComponent as LocationSvg } from "./assets/location.svg";
import parkingImg from "./assets/parking-placeholder.svg";

import { ParkingFeatureIcon } from "./parking-feature-icon";
import type { TransNamespace } from "../types";
import type { ParkingSearchResult } from "@mozioinc/query";

type ParkingCardProps = Omit<ParkingDialogTriggerProps, "children">;
const FEATURES_LIMIT = 3;

export const ParkingCard = (props: ParkingCardProps) => {
  const {
    name,
    location,
    logoUrl,
    rating,
    price,
    searchId,
    resultId,
    providerId,
    airportTransferSummary,
    features,
  } = props;
  const { t } = useTranslation<TransNamespace>("parking:search-result");

  return (
    <ParkingDialogTrigger {...props}>
      <Card className="h-full hover:shadow-lg">
        <CardHeader className="flex flex-row flex-wrap items-start gap-x-4">
          <div className="w-16">
            <AspectRatio ratio={1 / 1}>
              <img
                src={logoUrl || parkingImg}
                alt={`logo of ${name}`}
                className="h-full w-full rounded-md object-cover"
              />
            </AspectRatio>
          </div>
          <CardTitle className="flex-1 py-1">
            <p className="mr-2 inline">{name}</p>
            {rating && <Badge variant="ring">{rating}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 text-xs">
          <ul className="space-y-2 pb-3 pl-20 text-sm sm:pl-0">
            {location.lat !== undefined && location.lng !== undefined && (
              <li className="flex items-center gap-1 sm:gap-2">
                <LocationSvg className="text-neutral-800 sm:text-xl" />{" "}
                <Link
                  href={`https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`}
                  target="_blank"
                  onClick={(event) => event.stopPropagation()}
                >
                  {t("View on Map")}
                </Link>
              </li>
            )}

            <li className="flex items-center gap-1 sm:gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex gap-1 sm:gap-2">
                    <AirportTransferIcon
                      type={airportTransferSummary.type}
                      className="text-neutral-800 sm:text-xl"
                    />
                    {airportTransferSummary.name}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {airportTransferSummary.descriptions.map((description) => (
                    <p key={description}>{description}</p>
                  ))}
                </TooltipContent>
              </Tooltip>
            </li>

            {features.slice(0, FEATURES_LIMIT).map((feature) => (
              <li className="flex items-center gap-1 sm:gap-2" key={feature.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex gap-1 sm:gap-2">
                      <ParkingFeatureIcon
                        type={feature.type}
                        className="text-neutral-800 sm:text-xl"
                      />
                      {feature.name}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>{feature.description}</TooltipContent>
                </Tooltip>
              </li>
            ))}
          </ul>
        </CardContent>
        <CardFooter className="items-end justify-between border-t border-t-neutral-200">
          <p className="mr-1 text-xl font-semibold">
            {price.amount} <span className="text-xs">{price.currency}</span>
          </p>
          <Button asChild size={BUTTON_SIZE.MD} className="whitespace-nowrap">
            <LinkBase
              to="/booking"
              search={{
                searchId,
                resultId,
                providerId,
              }}
            >
              {t("Book Now")} <ChevronSvg className="text-xl" />
            </LinkBase>
          </Button>
        </CardFooter>
      </Card>
    </ParkingDialogTrigger>
  );
};

type ParkingDialogTriggerProps = {
  price: { amount: number; currency: string };
  detailOpen?: boolean;
  onDetailOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  searchId: string;
  resultId: string;
  providerId: string;
} & ParkingDetailProps;

const ParkingDialogTrigger = ({
  price,
  detailOpen,
  onDetailOpenChange,
  children,
  providerId,
  resultId,
  searchId,
  ...others
}: ParkingDialogTriggerProps) => {
  const { t } = useTranslation<TransNamespace>("parking:search-result");
  const greaterThenSmall = useMediaQuery(mediaQueries.sm);
  const mobileOpen = !greaterThenSmall && detailOpen;
  const desktopOpen = greaterThenSmall && detailOpen;

  return (
    <>
      <div className="inline sm:hidden">
        <Sheet open={mobileOpen} onOpenChange={onDetailOpenChange}>
          <SheetTrigger className="w-full text-left">{children}</SheetTrigger>
          <SheetContent
            side={SHEET_CONTENT_SIDE.BOTTOM}
            className="flex h-full flex-col rounded-none p-0"
          >
            <Link
              className="flex items-center p-3"
              onClick={() => onDetailOpenChange?.(false)}
            >
              <ChevronSvg className="rotate-180" />
              {t("Back")}
            </Link>
            <div className="flex-1 overflow-y-auto">
              <ParkingDetail {...others} />
            </div>
            <div className="flex flex-none items-end justify-between border-t border-t-neutral-200 p-3">
              <p className="text-2xl font-semibold">
                {price.amount} {price.currency}
              </p>
              <Button className="text-base" asChild>
                <LinkBase
                  to="/booking"
                  search={{
                    searchId,
                    resultId,
                    providerId,
                  }}
                >
                  {t("Book Now")}
                  <ChevronSvg className="text-xl" />
                </LinkBase>
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      <Dialog open={desktopOpen} onOpenChange={onDetailOpenChange}>
        <DialogTrigger className="text-left">{children}</DialogTrigger>
        <DialogPortal>
          <DialogContent className="max-w-xl">
            <DialogClose />
            <div className="flex-1 overflow-y-auto">
              <ParkingDetail {...others} />
            </div>
            <DialogFooter className="items-end border-t border-t-neutral-200 sm:justify-between ">
              <p className="text-2xl font-semibold">
                {price.amount} {price.currency}
              </p>
              <Button asChild>
                <LinkBase
                  to="/booking"
                  search={{
                    searchId,
                    resultId,
                    providerId,
                  }}
                >
                  {t("Book Now")}
                  <ChevronSvg className="text-xl" />
                </LinkBase>
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  );
};

type ParkingDetailProps = {
  name: string;
  logoUrl?: string;
  rating?: number;
  imageUrl?: string;
  description: string;
  basicInstructionsHtml: string;
  detailedInstructionsHtml?: string;
  location: ParkingSearchResult["location"];
  airportTransferSummary: ParkingSearchResult["airportTransferSummary"];
  features: ParkingSearchResult["features"];
};

const ParkingDetail = ({
  name,
  imageUrl,
  logoUrl,
  rating,
  location,
  // description,
  basicInstructionsHtml,
  detailedInstructionsHtml,
  airportTransferSummary,
  features,
}: ParkingDetailProps) => {
  const { t } = useTranslation<TransNamespace>("parking:search-result");

  return (
    <div>
      {imageUrl && (
        <AspectRatio ratio={16 / 5}>
          <img
            src={imageUrl}
            alt={`Parking ${name}`}
            className="h-full w-full rounded-t-lg object-cover"
          />
        </AspectRatio>
      )}

      <div className="flex flex-row items-start gap-x-3 p-6">
        <img
          src={logoUrl || parkingImg}
          alt={`logo of ${name}`}
          className="h-14 w-14 rounded-md object-cover"
        />
        <div className="flex flex-col gap-4">
          <h3 className="text-xl font-semibold sm:text-2xl">{name}</h3>
          {typeof rating === "number" && (
            <Badge
              variant="ring"
              className="gap-1 bg-transparent p-0 pr-1 text-base text-black"
            >
              <Badge>{rating}</Badge>
              {rating < 7 && t("Good")}
              {rating >= 7 && rating < 8 && t("Very Good")}
              {rating >= 8 && rating < 9 && t("Excellent")}
              {rating >= 9 && t("Exceptional")}
            </Badge>
          )}
          <ul className="space-y-2 text-sm">
            {location.lat !== undefined && location.lng !== undefined && (
              <li className="flex items-start gap-1">
                <LocationSvg className="flex-none text-xl text-neutral-800" />{" "}
                <Link
                  href={`https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`}
                  target="_blank"
                  onClick={(event) => event.stopPropagation()}
                >
                  {t("View on Map")}
                </Link>
              </li>
            )}

            <li className="items-top flex flex-none gap-1 sm:gap-2">
              <AirportTransferIcon
                type={airportTransferSummary.type}
                className="flex-none text-xl text-neutral-800"
              />
              {airportTransferSummary.descriptions.map((description) => (
                <p key={description}>{description}</p>
              ))}
            </li>

            {features.slice(0, FEATURES_LIMIT).map((feature) => (
              <li className="items-top flex gap-1 sm:gap-2" key={feature.id}>
                <ParkingFeatureIcon
                  type={feature.type}
                  className="flex-none text-xl text-neutral-800"
                />
                {feature.description}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Divider className="px-6" />

      {!!basicInstructionsHtml && (
        <div
          className="prose p-6 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_h3]:text-base"
          dangerouslySetInnerHTML={{ __html: basicInstructionsHtml }}
        />
      )}

      {!!detailedInstructionsHtml && (
        <>
          <Divider className="px-6" />

          <div
            className="prose p-6 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
            dangerouslySetInnerHTML={{ __html: detailedInstructionsHtml }}
          />
        </>
      )}

      {/* {!!description && <div className="p-4">{description}</div>} */}
    </div>
  );
};

const Divider = ({ className }: { className: string }) => (
  <div className={className}>
    <div className="border-t border-t-neutral-200" />
  </div>
);
